'use client'

import Image from 'next/image'
import { ComponentProps, PropsWithChildren, ReactNode } from 'react'
import { useIsClient } from 'usehooks-ts'

import PremiumTag from '@/app/[locale]/[roomId]/components/PremiumTag'
import IconLike from '@/components/assets/icon_like.svg'
import IconLikeBordered from '@/components/assets/icon_like_bordered.svg'
import IconLikeFilled from '@/components/assets/icon_like_filled.svg'
import IconWatch from '@/components/assets/icon_watch.svg'
import CountView from '@/components/CountView'
import DeviceIcons from '@/components/DeviceIcons'
import MoreAction from '@/components/MoreAction'
import { CardDescription, CardTitle } from '@/components/ui/card'
import ViverseCard from '@/components/ViverseCard'
import { useLikedRoom } from '@/hooks/useLikedRoom'
import { Link } from '@/i18n/navigation'
import { extractRoomIdFromUrl } from '@/lib/cms'
import { cn } from '@/lib/utils'
import roomDefaultImage from '@/public/images/room-default.png'
import { Room } from '@/services/cms'

type ActionWorldCardProps = PropsWithChildren<
  ComponentProps<typeof ViverseCard> & {
    info: Partial<Room>
    href?: string
    showMoreAction?: boolean
    moreActionContent?: ReactNode
    isLive?: boolean
    liveCount?: number
    customDesc?: string
    hideLiked?: boolean
    shareOnly?: boolean
    className?: string
    imageClassName?: string
    isCoOwnedRoom?: boolean
    isDragging?: boolean
    disableDrag?: boolean
    onAfterMutatingWorld?: (deleteRoomId?: string) => void
    stopSetAsFeatured?: boolean
    hidePremiumBadge?: boolean
  }
>

export default function ActionWorldCard({
  href,
  info,
  showMoreAction = false,
  moreActionContent,
  customDesc,
  hideLiked = false,
  shareOnly = false,
  isCoOwnedRoom = false,
  className,
  imageClassName,
  isDragging = false,
  disableDrag = true,
  onAfterMutatingWorld,
  stopSetAsFeatured = false,
  hidePremiumBadge = true,
  ...props
}: ActionWorldCardProps) {
  const {
    id,
    title,
    image = roomDefaultImage.src,
    view_count,
    like_count,
    hubs_url,
    owner,
    is_liked,
    is_live,
    external_link,
    is_voucher_room,
  } = info
  const { isLiked, handleClickLike } = useLikedRoom(id || '', href || hubs_url || '/', is_liked)
  const isClient = useIsClient()

  return (
    <ViverseCard {...props} className={className}>
      <ViverseCard.Content className="group/card mb-[6px] flex aspect-[1.72] w-full items-center align-middle lg:mb-2">
        {is_live && isClient && (
          <div className="absolute top-3 left-3 z-10">
            <Link
              href={extractRoomIdFromUrl(href || external_link || hubs_url || '/')}
              className="cursor-pointer"
            >
              <div className="caption flex items-center gap-2 rounded-full bg-[#00000099] px-2 text-white">
                <span className="bg-vive-live size-2 rounded-full" />
                Live
              </div>
            </Link>
          </div>
        )}

        {!hideLiked && isClient && (
          <div
            className="1.5xl:size-7 absolute top-[10px] right-3 z-10 size-6 opacity-0 transition-opacity duration-150 group-hover/card:opacity-100"
            onClick={handleClickLike}
          >
            {isLiked ? <IconLikeFilled /> : <IconLikeBordered />}
          </div>
        )}

        <Link
          href={extractRoomIdFromUrl(href || external_link || hubs_url || '/')}
          className="block size-full cursor-pointer"
          onClick={(e) => {
            // 🧬 need this to prevent link failed
            if (!disableDrag && !isDragging) {
              e.preventDefault()
            }
          }}
        >
          <Image
            src={image || roomDefaultImage.src}
            className={cn(
              'transition-all duration-300 select-none hover:scale-110 hover:brightness-110',
              imageClassName,
            )}
            fill
            alt="go-to-world"
            style={{ objectFit: 'cover' }}
          />
        </Link>
      </ViverseCard.Content>

      <ViverseCard.Header className="mt-0">
        <div className="flex items-center justify-between">
          <Link
            href={extractRoomIdFromUrl(href || external_link || hubs_url || '/')}
            className={cn(
              'cursor-pointer text-left',
              { 'w-[calc(100%-48px)]': !showMoreAction },
              { '1.5xl:w-[calc(100%-94px)] w-[calc(100%-68px)]': showMoreAction },
            )}
          >
            <CardTitle className="subheader-2 1.5xl:subheader-1 1.5xl:h-[32px] truncate font-medium text-white">
              {title}
            </CardTitle>
          </Link>
          <div className="flex">
            {info.preferred_devices && <DeviceIcons deviceType={info.preferred_devices} />}
            {showMoreAction && id && isClient && (
              <div className="flex items-center">
                <MoreAction
                  info={info}
                  shareOnly={shareOnly}
                  isCoOwnedRoom={isCoOwnedRoom}
                  onAfterMutatingWorld={onAfterMutatingWorld}
                  stopSetAsFeatured={stopSetAsFeatured}
                  isPremiumRoom={is_voucher_room}
                >
                  {moreActionContent}
                </MoreAction>
              </div>
            )}
          </div>
        </div>

        <div className="mt-0 flex justify-between">
          <Link
            href={`/profile/${owner?.community_user_id}`}
            className="block max-w-[calc(100%-98px)] cursor-pointer lg:max-w-[calc(100%-106px)]"
          >
            <CardDescription className="text-vive-primary-20 1.5xl:text-[14px] 1.5xl:tracking-[0.22px] truncate text-left">
              {customDesc ? customDesc : owner?.display_name}
            </CardDescription>
          </Link>

          {!external_link && isClient && (
            <div className="1.5xl:caption text-vive-primary-20 1.5xl:ml-3 flex gap-4 text-[10px]">
              <CountView icon={<IconWatch />} count={view_count ?? 0} />
              <CountView icon={<IconLike />} count={like_count ?? 0} />
            </div>
          )}
        </div>
        {!hidePremiumBadge && is_voucher_room && isClient && <PremiumTag className="mt-1" />}
      </ViverseCard.Header>
    </ViverseCard>
  )
}
